import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import Blank from "../../../../../DefaultStyles/Blank";
import { MailSender } from "./PartyMailList";
import { IMailDetail } from "../PartyMailModal";
import LineBtn from "../../../../../Button/LineBtn";
import AlertModal from "../../../../../Modal/AlertModal";
import { useApi } from "../../../../../../hooks/useApi";
import Loader from "../../../../../DefaultStyles/Loader";
import { toast } from "react-toastify";

interface PartyMailDetailProps {
  mailData: IMailDetail;
  mailStatus?: string;
  user?: string;
  party?: string;
  resendData?: {
    receiver: string;
    url: string;
    sender: string;
    id?: number;
  };
  goToList?: () => void;
  isSecondaryMail?: boolean;
}

interface SWarningIcon {
  mailStatus?: string;
}

export default function PartyMailDetail({
  mailData,
  mailStatus,
  user,
  party,
  resendData,
  goToList,
  isSecondaryMail,
}: PartyMailDetailProps) {
  console.log(mailData);
  console.log("🧡", resendData);

  const [resendAskAlert, setResendAskAlert] = useState(false);
  const [reSend, setReSend] = useState(false);

  const { response, loading, error } = useApi({
    method: "post",
    url: isSecondaryMail && reSend ? `party/mail/send` : undefined,
    data: {
      receiver: resendData?.receiver ?? "",
      sender: reSend
        ? "admin@linkidmail.net"
        : isSecondaryMail
        ? resendData?.receiver ?? ""
        : resendData?.sender ?? "",
      url: resendData?.url ?? "",
    },
  });

  const {
    response: newResponse,
    loading: newLoading,
    error: newError,
  } = useApi({
    method: "post",
    url: !isSecondaryMail && reSend ? `mail/send` : undefined,
    data: {
      receiver: resendData?.receiver ?? "",
      sender: reSend ? "admin@linkidmail.net" : resendData?.sender ?? "",
      id: resendData?.id ?? "",
    },
  });

  useEffect(() => {
    if (response?.status === 200 || newResponse?.status === 200) {
      toast.success("메일을 발송했습니다.");

      if (goToList) {
        goToList();
      }
    }
  }, [response, newResponse]);

  useEffect(() => {
    if (error || newError) {
      toast.error(
        newError?.data ?? error?.data ?? "메일 재전송에 실패했습니다."
      );
    }
  }, [error, newError]);

  // a태크 클릭시 뉴탭이 오픈되도록 한다.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "A") {
        e.preventDefault();
        const href = target.getAttribute("href");
        if (href) {
          window.open(href, "_blank", "noopener,noreferrer");
        }
      }
    };

    document.addEventListener("click", handleClick);
    // 이벤트 리스너 제거
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <>
      {party && (
        <WarningContainer>
          {mailStatus ? (
            <Status mailStatus={mailStatus}>
              {/* delivered: 포워드 성공 / accepted: 포워드 대기 / failed: 포워드 실패 / stored: 포워드 안함 (필터 데이터 기반)*/}
              <StatusIcon
                className="material-icons-outlined"
                style={{ marginRight: 4 }}
              >
                notifications
              </StatusIcon>
              <StatusText>
                {mailStatus?.includes("delivered")
                  ? `${
                      mailStatus?.includes("resent") ? "재발송 " : ""
                    }포워드 성공`
                  : mailStatus?.includes("accepted")
                  ? `${
                      mailStatus?.includes("resent") ? "재발송 " : ""
                    }포워드 대기`
                  : mailStatus?.includes("stored")
                  ? `${
                      mailStatus?.includes("resent") ? "재발송 " : ""
                    }포워드 안함 (필터 데이터 기반)`
                  : `${
                      mailStatus?.includes("resent") ? "재발송 " : ""
                    }포워드 실패`}
              </StatusText>
            </Status>
          ) : (
            <div />
          )}
          <LineBtn
            icon="send"
            btnText="재발송하기"
            btnHeight={38}
            btnClickEvent={() => setResendAskAlert(true)}
          />
        </WarningContainer>
      )}

      <PartyMailDetailContainer>
        <SenderContainer>
          <SenderLabel>보낸 사람</SenderLabel>

          <MailSender>{mailData?.from}</MailSender>

          <SendingDate>
            {dayjs(mailData?.date ?? undefined)?.format("YYYY.MM.DD HH:mm")}
          </SendingDate>
        </SenderContainer>

        <MailContent>
          <MailTitle>{mailData?.subject}</MailTitle>

          <MailDesc dangerouslySetInnerHTML={{ __html: mailData?.body }} />
        </MailContent>

        <Blank blankHeight={20} />
      </PartyMailDetailContainer>

      {resendAskAlert && (
        <AlertModal
          title={`${user}에게
          ${party} 파티 초대 메일을 다시 보내시겠습니까?`}
          cancelEvent={() => setResendAskAlert(false)}
          confirmText="보내기"
          confirmEvent={() => {
            setReSend(true);
            setResendAskAlert(false);
          }}
          btnColor="mint"
        />
      )}

      {(loading || newLoading) && <Loader />}
    </>
  );
}

const WarningContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Status = styled.div<SWarningIcon>`
  display: flex;
  align-items: center;
  color: ${({ theme, mailStatus }) =>
    mailStatus?.includes("delivered")
      ? theme.buttonColor.bluePressed
      : mailStatus?.includes("accepted")
      ? theme.color.yellow
      : theme.buttonColor.redNormal} !important;
`;

const StatusIcon = styled.span<SWarningIcon>`
  font-size: 24px;

  margin-right: 16px;
`;

const StatusText = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 19.5px;
  text-align: left;
`;

const PartyMailDetailContainer = styled.div`
  width: 100%;
  /* min-height: 360px; */
  height: auto;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.color.bgColor};
  padding: 18px 16px;
  margin-bottom: 40px;
  font-family: -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo",
    Pretendard Variable, Roboto, "Noto Sans KR", "Segoe UI", "Malgun Gothic",
    sans-serif !important;
`;

const SenderContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.buttonColor.lineNormal};
`;

const SenderLabel = styled.label`
  font-size: 13px;
  line-height: 19.5px;
  text-align: left;
  color: ${({ theme }) => theme.color.gray600};
  margin-bottom: 4px;
`;

const SendingDate = styled.span`
  margin-top: 8px;
  font-size: 13px;
  line-height: 19.5px;
  text-align: left;
  color: #858c97;
`;

const MailContent = styled.div`
  width: 100%;
  /* height: 100%; */
  min-height: 250px;
  height: auto;
  padding-top: 20px;
  overflow: scroll;
`;

const MailTitle = styled.div`
  font-size: 15px;
  font-weight: bold;
  line-height: 22.5px;
  text-align: left;
  color: ${({ theme }) => theme.color.gray700};
`;

const MailDesc = styled.div`
  margin-top: 16px;

  div {
    font-family: -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo",
      Pretendard Variable, Roboto, "Noto Sans KR", "Segoe UI", "Malgun Gothic",
      sans-serif !important;
    font-size: 13px !important;
    line-height: 19.5px !important;
    text-align: left;
    color: ${({ theme }) => theme.color.gray700} !important;
    white-space: pre-line;
    word-break: keep-all;
  }
`;
